/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import {mat4, vec3} from "gl-matrix";
import IAddress from "App/utils/globe/chunk_lod/adress";
import ProgressiveChunkLODNode from "App/utils/globe/chunk_lod/tree/progressive_chunk_lod_node";
import PriorityManager from "App/utils/globe/chunk_lod/priority_manager";
import VisibilityManager from "App/utils/globe/chunk_lod/visibility_manager";
import SynchronizationManager from "App/utils/globe/chunk_lod/synchronization_manager";
import DownloadManager from "App/utils/globe/chunk_lod/network/download_manager";
import DebugManager from "App/utils/globe/chunk_lod/debug_manager";
import ChunkLODTreeCreator from "App/utils/globe/chunk_lod/chunk_lod_tree_creator";
import convertBase64ToUint8Array from "App/utils/globe/base_64_to_uint_8";
import IGlobeWorker from "App/workers/globe_interface";

export enum EPatchManagementStateEnum {
    NOT_MANAGED = 0,
    DOWNLOADING_DESCRIPTION = 1,
    DESCRIPTION_READY = 2,
    MANAGED = 3
}

export default
class PatchManager {
    private _worker: IGlobeWorker;
    protected _baseAddress: IAddress;
    protected _root: TreeNode;
    protected _state: EPatchManagementStateEnum;
    protected _patchDescription: any;
    protected _annotation: any;
    protected _tops: Float32Array;
    protected _bottoms: Float32Array;
    protected _usedNodes: Uint8Array;
    protected _zeropointTranslationMatrix: mat4;

    private _priorityManager: PriorityManager;
    private _visibilityManager: VisibilityManager;
    private _synchronizationManager: SynchronizationManager;
    private _downloadManager: DownloadManager;
    private _debugManager: DebugManager;
    private _chunkLODTreeCreator: ChunkLODTreeCreator;

    constructor(aWorker: IGlobeWorker, aBaseAddress: IAddress) {
        this._worker = aWorker;
        this._baseAddress = aBaseAddress;

        this._root = null;
        this._chunkLODTreeCreator = new ChunkLODTreeCreator(this);
        this._downloadManager = new DownloadManager(this);
        this._synchronizationManager = new SynchronizationManager(this);
        this._visibilityManager = new VisibilityManager(this);
        this._priorityManager = new PriorityManager(this);
        this._debugManager = new DebugManager(this);

        this._state = EPatchManagementStateEnum.NOT_MANAGED;
        this._patchDescription = null;
        this._annotation = null;
        this._tops = null;
        this._bottoms = null;
        this._usedNodes = null;
        this._zeropointTranslationMatrix = null;
    }

    public getPatchInfo(): any {
        return this._baseAddress.patchInfo;
    }

    public getCenter(): vec3 {
        if (!this._root) {
            return vec3.create();
        }

        return this._root.getAddress().getCenter();
    }

    public getWorker(): IGlobeWorker {
        return this._worker;
    }

    public getBaseAddress(): IAddress {
        return this._baseAddress;
    }

    public getName(): string {
        return this._baseAddress.patchInfo.name;
    }

    public getPositionOffset(): number[] {
        return this._baseAddress.getTranslationOffset();
    }

    public getTops(): Float32Array {
        return this._tops;
    }

    public getBottoms(): Float32Array {
        return this._bottoms;
    }

    public getUsedNodes(): Uint8Array {
        return this._usedNodes;
    }

    public getZeroPointTranslation(): mat4 {
        return this._zeropointTranslationMatrix;
    }

    public setView(aView: any) {
        this._priorityManager.invalidatePriority();
        this._chunkLODTreeCreator.setView(aView);
        this._downloadManager.setView(aView);
        this._synchronizationManager.setView(aView);
    }

    public settledOnGPU(aMessage: any) {
        this._synchronizationManager.settledOnGPU(aMessage);
        this._visibilityManager.scheduleUpdate();
    }

    private _getRequireManagement(): boolean {
        // TODO Jarek we should render a planet via height maps and the from certain distance start rendering this patches
        // we need to take the patch height from the range in baseAddress and decide whether we should start rendering the patch
        // or just the heightmap
        return true;
    }

    protected _downloadDecription() {
        let self = this;
        let xhr = new XMLHttpRequest();
        // commente out the following code because it is not accepted by Firefox in worker
        /*if (xhr.overrideMimeType) {
            xhr.overrideMimeType('application/json');
        }*/

        xhr.onreadystatechange = () =>
        {
            if (xhr.readyState !== XMLHttpRequest.DONE) {
                return xhr;
            }

            if ((xhr.status !== 200) && (xhr.status !== 0)) {
                // TODO Jarek notify about failing download of the desription
            }

            if (xhr.status === 200) {
                this._patchDescription = JSON.parse(xhr.responseText);
                this._annotation = JSON.parse(this._patchDescription.Annotations[0].data);

                if (this._annotation.tops !== undefined) {
                    this._tops = new Float32Array(convertBase64ToUint8Array(this._annotation.tops));
                }
                if (this._annotation.heights !== undefined) {
                    // compatibility with old models
                    this._tops = new Float32Array(convertBase64ToUint8Array(this._annotation.heights));
                }

                if (this._annotation.bottoms !== undefined) {
                    this._bottoms = new Float32Array(convertBase64ToUint8Array(this._annotation.bottoms));
                }
                else {
                    this._bottoms = new Float32Array(this._tops.length);
                    for (let i = 0; i < this._tops.length; i++) {
                        this._bottoms[i] = this._tops[i] - 30;
                    }
                }

                this._usedNodes = new Uint8Array(convertBase64ToUint8Array(this._annotation.usedNodes));

                this._zeropointTranslationMatrix = this._patchDescription.ZeropointSettings.translationMat;
                this._state = EPatchManagementStateEnum.DESCRIPTION_READY;

                this._baseAddress.setTranslationOffset([this._zeropointTranslationMatrix[12], this._zeropointTranslationMatrix[14]]);
                this._root = new ProgressiveChunkLODNode(this, null, this.getBaseAddress());
            }
        };

        let url = this._baseAddress.getBaseURL() + "/" + "/modelDescription.json";
        xhr.open('GET', encodeURI(url), true);
        xhr.send(null);
    }

    public update() {
        let requireManagement = this._getRequireManagement();
        if (this._state === EPatchManagementStateEnum.NOT_MANAGED) {

            if (requireManagement) {
                this._state = EPatchManagementStateEnum.DOWNLOADING_DESCRIPTION;
                this._downloadDecription();
            }

            return;
        }

        if (this._state === EPatchManagementStateEnum.DESCRIPTION_READY) {
            this._state = EPatchManagementStateEnum.MANAGED;
        }

        if (this._state !== EPatchManagementStateEnum.MANAGED) {
            return;
        }

        if (!requireManagement) {
            // do at least everything to finish the manging and set state, for not let run the update last time
            this._state = EPatchManagementStateEnum.DESCRIPTION_READY;
            // so no call return here
        }

        // generate add/remove nodes requests
        this._root.accept(this._chunkLODTreeCreator);

        // save nodes to remove
        let nodesToRemove = this._chunkLODTreeCreator.getNodesToRemove();
        //if (nodesToRemove.length > 0) {
        this._visibilityManager.scheduleUpdate();
        //}

        // perform add/remove requests of nodes in the chunk lod tree
        this._chunkLODTreeCreator.update();

        // recompute priority
        this._priorityManager.invalidatePriority(); // TODO jarek do we need that?
        this._root.accept(this._priorityManager);

        // download/abort data

        this._root.accept(this._downloadManager);
        this._downloadManager.update(nodesToRemove);

        // prepare requests for synchronization of data
        this._root.accept(this._synchronizationManager);
        this._synchronizationManager.update(nodesToRemove);

        // prepare request for visibility update
        if (this._visibilityManager.needUpdate()) {
            this._visibilityManager.clearVisibility();
            this._root.accept(this._visibilityManager);
            this._visibilityManager.update();
        }

        if (this._worker.getDebugEnabled()) {
            this._debugManager.clearData();
            this._root.accept(this._debugManager);
            this._debugManager.update();
        }
    }

    getPriorityManager(): PriorityManager {
        return this._priorityManager
    }

    getVisibilityManager(): VisibilityManager {
        return this._visibilityManager;
    }
}
